import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { isToggledMapContentEntry, mapContentEntriesById, type MapContentId } from '@/components/AdvancedMapping/mapContent/mapContentRegistry';
import type { MapContentVisibility } from '@/components/AdvancedMapping/mapContent/mapContentVisibility';
import type { PoiGroupId } from '@/components/AdvancedMapping/mapContent/poiGroups';

export type AdvancedMappingState = {
  contentVisibility: MapContentVisibility;
  /** Exclusions rather than inclusions, so a group added later is on for everyone. */
  excludedPoiGroups: PoiGroupId[];
};

export const initialState: AdvancedMappingState = {
  contentVisibility: {},
  excludedPoiGroups: []
};

export const advancedMappingSlice = createSlice({
  name: 'advancedMapping',
  initialState,
  reducers: {
    toggleContentVisibility: (state, action: PayloadAction<MapContentId>) => {
      const entry = mapContentEntriesById[action.payload];
      if (isToggledMapContentEntry(entry)) {
        return;
      }
      // Back at the default, delete the key so a later default change still applies
      const isVisible = !(state.contentVisibility[entry.id] ?? entry.defaultVisible);
      if (isVisible === entry.defaultVisible) {
        delete state.contentVisibility[entry.id];
      } else {
        state.contentVisibility[entry.id] = isVisible;
      }
    },
    setExcludedPoiGroups: (state, action: PayloadAction<PoiGroupId[]>) => {
      state.excludedPoiGroups = action.payload;
    },
    hydrateAdvancedMapping: (state, action: PayloadAction<Partial<AdvancedMappingState>>) => ({ ...state, ...action.payload })
  }
});

export const { hydrateAdvancedMapping, setExcludedPoiGroups, toggleContentVisibility } = advancedMappingSlice.actions;

export default advancedMappingSlice.reducer;
